/*
 * File: elmoApi.ts
 * Project: rosita
 * -----
 * Last Modified: 24 Feb 2022 11:31:37 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react';
import { LineItemErrorObject } from 'components/Create/common/ActionControls';
import lodash from 'lodash';
import { ApiMultipleResponse } from 'models/ApiModels';
import { Attachment, CreateAttachmentPayload, DeleteAttachmentPayload } from 'models/Attachment';
import { DeploymentRequestParams, EarliestRequestDeliveryDateParams } from 'models/DeploymentRequest';
import { PostedSpareOrder, SparingOrder } from 'models/PostedSpareOrder';
import { LineItemPayload } from 'models/Product';
import { SparingOrderPayload, SparingOrderTrackingPayload, SparingRequest, SparingRequestPayload } from 'models/SparingRequests';
import { TimeZoneData } from 'models/Timezone';
import { setPostRequestLoadingMessage } from 'redux/slices/appSlice';
import { setDeliveryAddressTimeZoneInfo, setFullOutageAddress, setFullShippingAddress } from 'redux/slices/formSlice';
import { ReduxState } from 'redux/store';

/* ******************** Base Query ******************** */
const baseUrl = process.env.REACT_APP_TWITTLEBUGS_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_TWITTLEBUGS;

export const twittlebugsBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const divisionId = (api.getState() as ReduxState).app.acuityContext?.selectedCustomer.id;
  const isNormalBaseUrl = typeof args !== 'string' && (args.url === '/diagnostics/version' || args.url === '/diagnostics/apiName' || args.url.includes('timeZone'));
  // const isBirst = typeof args !== 'string' && args.url === '/birstToken';

  if (!divisionId && !isNormalBaseUrl) {
    return {
      error: {
        status: 400,
        statusText: 'Bad Request',
        data: 'No division ID received'
      }
    };
  }
  const urlEnd = typeof args === 'string' ? args : args.url;
  const adjustedUrl = isNormalBaseUrl ? args.url : `divisions/${divisionId}/${urlEnd}`;
  const adjustedArgs = typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };

  return fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as ReduxState).app.accessToken;

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        headers.set('x-functions-key', functionsKey);

        if (api.endpoint === 'createAttachment') {
          // headers.set('Content-Type', 'multipart/form-data');
        } else {
          headers.set('Content-Type', 'application/json');
        }
      }

      return headers;
    }
  })(adjustedArgs, api, extraOptions);
};

export const sparingRequestApi = createApi({
  reducerPath: 'sparingRequestApi',
  baseQuery: twittlebugsBaseQuery,
  tagTypes: ['SparingRequests', 'SparingRequest', 'Orders'],
  endpoints: (builder) => ({
    getSparingRequests: builder.query<ApiMultipleResponse<SparingRequest>, DeploymentRequestParams>({
      providesTags: ['SparingRequests'],
      query: (params) => ({
        url: 'sparingRequests?orderByDirection=descending',
        params: ((): Partial<DeploymentRequestParams> => {
          const filteredObject = lodash.pickBy(params, (v) => v !== undefined);

          return filteredObject;
        })()
      }),

      onQueryStarted: async (_arg, _api) => {
        try {
          await _api.queryFulfilled;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().sparingRequestApi;

          const lastQueryArgs = { ...originalArgs, offset: _arg.offset - 25 };
          const lastQueryData = Object.values(queries).find((value) => lodash.isEqual(value?.originalArgs, lastQueryArgs));

          if (lastQueryData) {
            _api.dispatch(
              sparingRequestApi.util.updateQueryData('getSparingRequests', _arg, (draft) => {
                draft.data.unshift(...(lastQueryData.data as any).data);
              })
            );
          }
        } catch (error) {
          console.error(error);
        }
      }
    }),
    getSparingOrders: builder.query<ApiMultipleResponse<SparingOrder>, string>({
      providesTags: ['Orders'],
      queryFn: async (arg, { dispatch }, extraOptions, baseQuery) => {
        const response = (await baseQuery({ url: `sparingRequests/${arg}/sparingOrders` })) as QueryReturnValue<ApiMultipleResponse<SparingOrder>, FetchBaseQueryError, FetchBaseQueryMeta>;

        if (response.error) {
          return { error: response.error };
        }

        return { data: { ...response.data, data: response.data.data } };
      }
    }),
    getSparingRequest: builder.query<SparingRequest, string>({
      providesTags: ['SparingRequest'],
      query: (sparingId) => ({
        url: `sparingRequests/${sparingId}`
      }),
      onQueryStarted: async (arg, { queryFulfilled, dispatch, getState }) => {
        const state = getState() as ReduxState;

        if (state.form.fullOutageAddress) {
          dispatch(setFullOutageAddress(state.form.fullOutageAddress));

          return;
        }
        if (state.form.fullShippingAddress) {
          dispatch(setFullShippingAddress(state.form.fullShippingAddress));

          return;
        }

        try {
          const data = await queryFulfilled;

          const latitude = data?.data?.outageLocation?.latitude;
          const longitude = data?.data?.outageLocation?.longitude;

          if (latitude && longitude) {
            try {
              const timezoneResp = await fetch(`${process.env.REACT_APP_TWITTLEBUGS_BASE_URL}/timeZone/${latitude}/${longitude}`, {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  'x-functions-key': process.env.REACT_APP_API_HOST_KEY_TWITTLEBUGS,
                  authorization: `Bearer ${state.app.accessToken}`,
                  'Access-Control-Allow-Origin': '*'
                }
              });
              const timezoneData = await timezoneResp.json();

              if (timezoneData) {
                dispatch(setDeliveryAddressTimeZoneInfo(timezoneData));
              }
            } catch (error) {
              console.error(error);
            }
          }

          dispatch(setFullOutageAddress(data.data.outageLocation));
          dispatch(setFullShippingAddress(data.data.shippingLocation));
        } catch (error) {
          console.error(error);
        }
      }
    }),
    createSparingRequest: builder.mutation<SparingRequest, SparingRequestPayload>({
      invalidatesTags: ['SparingRequests'],
      query: (data) => ({
        url: 'sparingRequests',
        method: 'POST',
        body: data
      })
    }),
    resendSparingOrderNotifications: builder.mutation<SparingRequest, { payload: SparingOrder; sparingRequestId: string; sparingOrderId: string }>({
      invalidatesTags: ['Orders'],
      query: ({ payload, sparingRequestId, sparingOrderId }) => ({
        url: `/sparingRequests/${sparingRequestId}/sparingOrders/${sparingOrderId}/resendNotification`,
        method: 'POST',
        body: payload
      })
    }),
    postSparingRequest: builder.mutation<{ data: PostedSpareOrder; updatedLineItems: LineItemErrorObject[] }, LineItemPayload>({
      invalidatesTags: ['SparingRequests', 'SparingRequest'],
      queryFn: async (arg, queryApi, extraOptions, baseQuery) => {
        const { sparingRequestId, sparingResponse } = arg;

        const lineItemIds = sparingResponse.lineItems.map((lineItem) => lineItem.id);

        const updatedLineItems: LineItemErrorObject[] = [];

        // queryApi.dispatch(setPostRequestLoadingMessage('Validating Sparing Request Header Information...'));

        // const validateHeader = await baseQuery(`sparingRequests/${sparingRequestId}/validate`);

        // if (validateHeader.error) {
        //   queryApi.dispatch(setPostRequestLoadingMessage(undefined));

        //   return { error: validateHeader.error, status: validateHeader?.error?.status };
        // }

        // queryApi.dispatch(setPostRequestLoadingMessage('Validating Sparing Request Line Items...'));

        // const validateLineItems = (await Promise.all(
        //   lineItemIds.map(async (lineItemId) => await baseQuery(`sparingRequests/${sparingRequestId}/lineItems/${lineItemId}/validate`))
        // )) as QueryReturnValue<boolean, FetchBaseQueryError, FetchBaseQueryMeta>[];

        // const lineError = validateLineItems.find((response) => response.error);

        // if (lineError?.error) {
        //   queryApi.dispatch(setPostRequestLoadingMessage(undefined));

        //   return { error: lineError.error, status: lineError?.error?.status };
        // }

        queryApi.dispatch(setPostRequestLoadingMessage('Posting Sparing Request...'));

        const response = (await baseQuery({ url: `sparingRequests/${sparingRequestId}/submit`, method: 'POST' })) as QueryReturnValue<PostedSpareOrder, FetchBaseQueryError, FetchBaseQueryMeta>;

        if (response.error) {
          queryApi.dispatch(setPostRequestLoadingMessage(undefined));

          return { error: response.error, status: response?.error?.status };
        }

        if (sparingResponse.attachments.length) {
          queryApi.dispatch(setPostRequestLoadingMessage('Posting Attachments...'));

          const response = (await baseQuery({ url: `/sparingRequests/${sparingRequestId}/attachments/submit`, method: 'POST' })) as QueryReturnValue<
            PostedSpareOrder,
            FetchBaseQueryError,
            FetchBaseQueryMeta
          >;

          if (response.error) {
            queryApi.dispatch(setPostRequestLoadingMessage(undefined));

            return { error: response.error, status: response?.error?.status };
          }
        }

        // for await (const [idx, lineItemId] of lineItemIds.entries()) {
        //   queryApi.dispatch(setPostRequestLoadingMessage(`Posting Sparing Request Line Items ${idx + 1} of ${lineItemIds.length}`));

        //   const currentLineItem = sparingResponse.lineItems.find((lineItem) => lineItem.id === lineItemId);
        //   const response = await baseQuery({ url: `sparingRequests/${sparingRequestId}/lineItems/${lineItemId}`, method: 'POST' });

        //   if (response.error) {
        //     updatedLineItems.push({
        //       key: lineItemId ?? '',
        //       error: response.error,
        //       lineProductName: currentLineItem?.productNumber ?? '',
        //       lineMessage: (response?.error as QueryErrorModel)?.data?.errorMessage ?? 'ERROR'
        //     });

        //     continue;
        //   }
        //   updatedLineItems.push({ key: lineItemId ?? '', lineProductName: currentLineItem?.productNumber ?? '', lineMessage: 'SUCCESS' });
        // }

        queryApi.dispatch(setPostRequestLoadingMessage(undefined));

        return { data: { data: response.data as PostedSpareOrder, updatedLineItems } };
      }
    }),
    updateSparingRequest: builder.mutation<SparingRequest, { id: string; body: SparingRequestPayload }>({
      invalidatesTags: ['SparingRequests', 'SparingRequest'],
      query: ({ id, body }) => ({
        url: `sparingRequests/${id}`,
        method: 'PUT',
        body
      })
    }),
    updateSparingOrder: builder.mutation<SparingOrder, { sparingRequestId: string; sparingOrderid: string; body: SparingOrderPayload }>({
      invalidatesTags: ['Orders'],
      query: ({ sparingRequestId, sparingOrderid, body }) => ({
        url: `sparingRequests/${sparingRequestId}/sparingOrders/${sparingOrderid}/fulfillmentInformation`,
        method: 'PUT',
        body
      })
    }),
    updateSparingOrderTrackingInformation: builder.mutation<SparingOrder, { sparingRequestId: string; sparingOrderid: string; body: SparingOrderTrackingPayload }>({
      invalidatesTags: ['Orders'],
      query: ({ sparingRequestId, sparingOrderid, body }) => ({
        url: `sparingRequests/${sparingRequestId}/sparingOrders/${sparingOrderid}/trackingInformation`,
        method: 'PUT',
        body
      })
    }),
    deleteSparingRequest: builder.mutation<void, { id: string; params: { wouldYouLikeToPlayAGame: boolean } }>({
      invalidatesTags: ['SparingRequests'],
      query: ({ id, params }) => ({
        url: `sparingRequests/${id}`,
        method: 'DELETE',
        params
      })
    }),
    recoverSparingRequest: builder.mutation<SparingRequest, string>({
      query: (id) => ({
        url: `sparingRequests/${id}/recover`,
        method: 'GET'
      }),
      invalidatesTags: ['SparingRequests', 'SparingRequest']
    }),
    createAttachment: builder.mutation<Attachment, CreateAttachmentPayload>({
      query: ({ id, file }) => ({
        url: `sparingRequests/${id}/attachments`,
        method: 'POST',
        body: file
      }),
      invalidatesTags: ['SparingRequests', 'SparingRequest']
    }),
    addUserToSmsNotifications: builder.mutation<SparingRequest, { id: string; payload: object }>({
      query: ({ id, payload }) => ({
        url: `sparingRequests/${id}/SmsNotifications`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['SparingRequests', 'SparingRequest']
    }),
    // eslint-disable-next-line @typescript-eslint/ban-types
    getBirstSSOToken: builder.query<{ accessToken: string }, void>({
      queryFn: async (arg, queryApi, extraOptions, baseQuery) => {
        const resp = (await baseQuery({
          url: `/birstToken`,
          method: 'POST',
          body: {}
        })) as QueryReturnValue<{ accessToken: string }, FetchBaseQueryError, FetchBaseQueryMeta>;

        if (resp.error) {
          return { error: resp.error };
        }

        return { data: resp.data };
      }
    }),
    postAttachment: builder.mutation<Attachment, { id: string; fileName: string }>({
      query: ({ id, fileName }) => ({
        url: `sparingRequests/${id}/attachments/${fileName}`,
        method: 'POST'
      }),
      invalidatesTags: ['SparingRequests']
    }),
    deleteAttachment: builder.mutation<Attachment, DeleteAttachmentPayload>({
      query: ({ id, fileName }) => ({
        url: `sparingRequests/${id}/attachments/${fileName}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['SparingRequests', 'SparingRequest']
    }),
    removeUserFromSmsNotifications: builder.mutation<SparingRequest, string>({
      query: (id) => ({
        url: `sparingRequests/${id}/SmsNotifications`,
        method: 'DELETE'
      }),
      invalidatesTags: ['SparingRequests', 'SparingRequest']
    }),
    getTimeZoneInfo: builder.query<TimeZoneData, { latitude: number; longitude: number }>({
      query: ({ latitude, longitude }) => ({
        url: `timeZone/${latitude}/${longitude}`
      })
    }),
    getEarliestRequestDeliveryDate: builder.query<{ earliestAvailableStandardDeliveryDate: string; earliestAvailableExpeditedDeliveryDate: string }, EarliestRequestDeliveryDateParams>({
      query: ({ deploymentRequestTypeId, destinationCountryCode, destinationSubdivisionCode, destinationPostalCode }) => ({
        url: `/deploymentRequestTypes/${deploymentRequestTypeId}/earliestAvailableDeliveryDates?destinationCountryCode=${destinationCountryCode}&destinationSubdivisionCode=${destinationSubdivisionCode}&destinationPostalCode=${destinationPostalCode}`,
        method: 'GET'
      })
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        method: 'GET',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getInfo: builder.query<{ version: string; apiName: string }, void>({
      async queryFn(args, queryApi, extraOptions, fetchWithBQ): Promise<any> {
        const version = await fetchWithBQ({ url: '/diagnostics/version', responseHandler: (response): Promise<string> => response.text() });
        const apiName = await fetchWithBQ({ url: '/diagnostics/apiName', responseHandler: (response): Promise<string> => response.text() });

        return { data: { version: version.data, apiName: apiName.data } };
      }
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        method: 'GET',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const {
  useGetSparingRequestsQuery,
  useCreateAttachmentMutation,
  useCreateSparingRequestMutation,
  useDeleteAttachmentMutation,
  useDeleteSparingRequestMutation,
  usePostAttachmentMutation,
  useGetSparingRequestQuery,
  useLazyGetSparingRequestQuery,
  useRecoverSparingRequestMutation,
  useUpdateSparingRequestMutation,
  usePostSparingRequestMutation,
  useGetEarliestRequestDeliveryDateQuery,
  useGetSparingOrdersQuery,
  // useGetSparingOrdersForPostedViewQuery,
  useGetBirstSSOTokenQuery,
  useUpdateSparingOrderMutation,
  useUpdateSparingOrderTrackingInformationMutation,
  useGetVersionQuery,
  useGetApiNameQuery,
  useResendSparingOrderNotificationsMutation,
  useGetTimeZoneInfoQuery,
  useGetInfoQuery,
  useAddUserToSmsNotificationsMutation,
  useRemoveUserFromSmsNotificationsMutation
} = sparingRequestApi;
